import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './ChangePasswordModal.module.css';
import api, { API_BASE_URL } from '../../../services/api';

const ChangePasswordModal = ({ open, onClose }) => {
  const [form, setForm] = useState({
    phone: '',
    code: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (!countdown) return;
    const timer = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  if (!open) return null;

  const resetState = () => {
    setForm({ phone: '', code: '', newPassword: '', confirmPassword: '' });
    setErrors({});
    setSending(false);
    setSubmitting(false);
    setCountdown(0);
    setMessage('');
    setShowNew(false);
    setShowConfirm(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '', general: '' }));
  };

  const handleSendCode = async () => {
    const phone = form.phone.trim();
    if (!phone) {
      setErrors((prev) => ({ ...prev, phone: '请输入手机号' }));
      return;
    }
    if (!/^\d{6,}$/.test(phone)) {
      setErrors((prev) => ({ ...prev, phone: '请输入正确的手机号' }));
      return;
    }
    setSending(true);
    setErrors((prev) => ({ ...prev, general: '' }));
    try {
      await api.post(`${API_BASE_URL}/send_sms_code/`, { phone });
      setCountdown(60);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || '验证码发送失败';
      setErrors((prev) => ({ ...prev, general: msg }));
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const phone = form.phone.trim();
    const code = form.code.trim();
    const newPwd = form.newPassword;
    const confirmPwd = form.confirmPassword;
    const nextErrors = {};

    if (!phone) nextErrors.phone = '请输入手机号';
    else if (!/^\d{6,}$/.test(phone)) nextErrors.phone = '请输入正确的手机号';
    if (!code) nextErrors.code = '请输入验证码';
    if (!newPwd || newPwd.length < 6) nextErrors.newPassword = '密码至少6位';
    if (newPwd !== confirmPwd) nextErrors.confirmPassword = '两次输入的密码不一致';

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSubmitting(true);
    setErrors((prev) => ({ ...prev, general: '' }));
    try {
      await api.post('/api/auth/change-password-sms', {
        phone,
        code,
        new_password: newPwd,
      });
      setMessage('密码已更新，请用新密码登录');
      resetState();
      onClose();
      alert('密码已更新，请使用新密码登录');
      localStorage.removeItem('token');
      window.location.href = '/login';
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || '修改失败，请重试';
      setErrors((prev) => ({ ...prev, general: msg }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.backdrop} role="presentation" onClick={handleClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className={styles.header}>
          <div>
            <h2 id="change-password-title" className={styles.title}>手机验证修改密码</h2>
            <p className={styles.subtitle}>获取验证码后重置登录密码</p>
          </div>
          <button type="button" className={styles.closeBtn} onClick={handleClose} aria-label="关闭">
            <span className="material-symbols-rounded">close</span>
          </button>
        </header>

        <form className={styles.form} onSubmit={handleSubmit}>
          <label className={styles.label} htmlFor="phone">
            手机号
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            className={`${styles.input} ${errors.phone ? styles.inputError : ''}`}
            value={form.phone}
            onChange={handleChange}
            placeholder="请输入绑定手机号"
            autoComplete="tel"
            disabled={sending || submitting}
          />
          {errors.phone && <div className={styles.error}>{errors.phone}</div>}

          <label className={styles.label} htmlFor="code">
            短信验证码
          </label>
          <div className={styles.codeRow}>
            <input
              id="code"
              name="code"
              type="text"
              className={`${styles.input} ${errors.code ? styles.inputError : ''}`}
              value={form.code}
              onChange={handleChange}
              placeholder="请输入验证码"
              disabled={submitting}
            />
            <button
              type="button"
              className={styles.codeBtn}
              onClick={handleSendCode}
              disabled={sending || countdown > 0 || submitting}
            >
              {countdown > 0 ? `重新发送(${countdown}s)` : '获取验证码'}
            </button>
          </div>
          {errors.code && <div className={styles.error}>{errors.code}</div>}

          <label className={styles.label} htmlFor="newPassword">
            新密码
          </label>
          <div className={styles.inputWrap}>
            <input
              id="newPassword"
              name="newPassword"
              type={showNew ? 'text' : 'password'}
              className={`${styles.input} ${styles.inputWithIcon} ${errors.newPassword ? styles.inputError : ''}`}
              value={form.newPassword}
              onChange={handleChange}
              placeholder="至少 6 位"
              autoComplete="new-password"
              disabled={submitting}
            />
            <button
              type="button"
              className={styles.eyeBtn}
              onClick={() => setShowNew((v) => !v)}
              disabled={submitting}
              aria-label={showNew ? '隐藏密码' : '显示密码'}
            >
              <span className="material-symbols-rounded">
                {showNew ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>
          {errors.newPassword && <div className={styles.error}>{errors.newPassword}</div>}

          <label className={styles.label} htmlFor="confirmPassword">
            再次输入新密码
          </label>
          <div className={styles.inputWrap}>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              className={`${styles.input} ${styles.inputWithIcon} ${errors.confirmPassword ? styles.inputError : ''}`}
              value={form.confirmPassword}
              onChange={handleChange}
              placeholder="再次输入新密码"
              autoComplete="new-password"
              disabled={submitting}
            />
            <button
              type="button"
              className={styles.eyeBtn}
              onClick={() => setShowConfirm((v) => !v)}
              disabled={submitting}
              aria-label={showConfirm ? '隐藏密码' : '显示密码'}
            >
              <span className="material-symbols-rounded">
                {showConfirm ? 'visibility_off' : 'visibility'}
              </span>
            </button>
          </div>
          {errors.confirmPassword && <div className={styles.error}>{errors.confirmPassword}</div>}

          {errors.general && <div className={styles.serverMessage}>{errors.general}</div>}
          {message && <div className={styles.serverMessage}>{message}</div>}

          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={handleClose} disabled={submitting}>
              取消
            </button>
            <button type="submit" className={styles.submitBtn} disabled={submitting}>
              {submitting ? '提交中…' : '确认修改'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

ChangePasswordModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ChangePasswordModal;
